/* stowedNodes.js — nodes stowed away (hidden) on PRODUCTION only.
 *
 * The Psilocybin / Neural Rewire consumable node is not surfaced to prod users yet
 * (Edward 2026-07-11: "on prod psilocybin should be hidden, stow it away"). It stays
 * VISIBLE on dev + preprod so the node and its canonical toast remain iterable.
 *
 * Filtering here — at the normalized skill-tree graph, the single upstream source —
 * removes the node from EVERY skill-tree surface at once: the tree itself and the
 * ProgressMap that SkillTreeViewport feeds it (ProgressMap is presentational and
 * renders whatever mapData it is handed). Any edge touching a stowed node is pruned
 * so no dangling reference survives. Data/ownership is untouched — this is a render
 * stow, not a delete, so unhiding is a one-liner (drop the slug / flip the host gate).
 *
 * Gate: FAIL-CLOSED. Stow unless the origin is positively recognised as a dev or
 * preprod surface (see `utils/originKind.js`).
 *
 * It used to be the other way round — `hostname === debono.ai`, everything else
 * shows — and that leaked the node twice over. A tailnet address reaching the
 * PROD container is not `debono.ai`, so the node rendered on production data;
 * and a packaged native shell reports its hostname as `localhost`, so it would
 * have shipped the node visible in any binary. Both are the same fail-open
 * shape: naming the origins you hide on, and defaulting to showing.
 *
 * Read the direction of the gate before you touch it. A new production hostname
 * must never be able to open this surface by being unrecognised.
 */

import { isProductionSurface } from '../../utils/originKind';

export const STOWED_ON_PROD_SLUGS = new Set(['psilocybin']);

export function isProdHost() {
  return isProductionSurface();
}

function nodeSlug(n) {
  return String(
    (n && (n.drug_slug
      || (n.payloadData && n.payloadData.drug_slug)
      || (n._stm && n._stm.payloadData && n._stm.payloadData.drug_slug)
      || n.id))
      || '',
  ).toLowerCase();
}

/**
 * Remove prod-stowed nodes (and any edges touching them) from a normalized
 * skill-tree graph. `isProd` is injectable so it can be unit-tested without a
 * real prod hostname; it defaults to the live host check.
 */
export function stowProdHiddenNodes(graph, isProd = isProdHost()) {
  if (!isProd || !graph || !Array.isArray(graph.nodes) || !graph.nodes.length) return graph;
  const nodes = graph.nodes.filter((n) => !STOWED_ON_PROD_SLUGS.has(nodeSlug(n)));
  if (nodes.length === graph.nodes.length) return graph;
  const ids = new Set(nodes.map((n) => n.id));
  const edges = Array.isArray(graph.edges)
    ? graph.edges.filter((e) => ids.has(e.from) && ids.has(e.to))
    : graph.edges;
  return { ...graph, nodes, edges };
}
